import {
  put,
  call,
  takeLeading,
  takeLatest,
  all,
  select,
} from 'redux-saga/effects';

import accountApi, {
  getProfileDetails as apiGetProfile,
  changePassword as accountApiChangePassword,
  updateContactEmail,
} from '../../../../../util/api/AccountApiClient';
import * as AuthHubClient from '../../../../../util/api/AuthHubClient';
import * as mainActions from '../../../reducers/ducks/legacy/MainDuck';
import * as authActions from '../../../reducers/ducks/legacy/AuthorizationDuck';
import * as profileActions from '../../../reducers/ducks/legacy/ProfileDuck';
import {
  toggleGlobalLoaderQueue,
  updateUser,
  setNavigateTo,
} from '../../../reducers/ducks/legacy/MainDuck';
import Notification from '../../../../../components/layout/Notification';
import notifyBugsnag from '../../../../../util/notifyBugsnag';
import { serializeItem } from '../../../../../util/Helpers';
import { setSelectedAddress } from '../../../reducers/ducks/new/Address/AddressReducer';
import {
  setPendingCheckoutOnSignup,
  setCheckoutContinuation,
  setAddressResponse,
} from '../../../reducers/ducks/legacy/OrderDuck';
import { getPhonesSaga, getAddressesSaga } from './ProfileSaga';

export function* authorize({ payload }) {
  try {
    yield put(toggleGlobalLoaderQueue({ name: 'authorize', status: true }));
    yield put(authActions.updateLoadingStatus(true));

    const loginResult = yield call(
      AuthHubClient.login,
      payload.email,
      payload.password
    );

    if (!loginResult.success) {
      throw new Error(loginResult.message || 'Login failed');
    }

    try {
      const eatzyTokenResult = yield call(
        AuthHubClient.getEatzyAccessToken,
        payload.email,
        payload.password
      );

      if (eatzyTokenResult.success) {
        yield put(
          authActions.setEatzyTokens({
            accessToken: eatzyTokenResult.accessToken,
            refreshToken: eatzyTokenResult.refreshToken,
            accessExpires: eatzyTokenResult.accessExpires,
          })
        );

        yield put(
          authActions.setAccessToken({
            token: eatzyTokenResult.accessToken,
            expires: eatzyTokenResult.accessExpires,
          })
        );
        yield put(
          authActions.setRefreshToken({
            token: eatzyTokenResult.refreshToken,
            expires: eatzyTokenResult.refreshExpires,
          })
        );
      }
    } catch (eatzyErr) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Auth] Eatzy access token fetch failed (non-blocking):',
        eatzyErr.message
      );
    }

    yield put(authActions.setSessionToken(loginResult.token));
    yield put(authActions.setAuthUser(loginResult.user));

    const user = yield call(apiGetProfile);
    yield all([call(getPhonesSaga), call(getAddressesSaga)]);
    yield put(authActions.updateSuccessStatus(true));
    yield put(updateUser(user));
    yield put(authActions.updateError(null));

    const pendingCheckout = yield select(
      (state) => state.order?.pendingCheckoutOnSignup
    );
    if (pendingCheckout) {
      yield put(setPendingCheckoutOnSignup(false));
      yield put(setCheckoutContinuation(true));
    }

    if (payload && payload?.callback) {
      payload.callback();
    }
    yield put(setNavigateTo(''));
  } catch (error) {
    const errorData = error?.response?.data || error;
    yield put(authActions.updateSuccessStatus(false));
    yield put(authActions.updateError(serializeItem(errorData)));
    yield put(authActions.setSessionToken(null));
    yield put(authActions.setAccessToken(null));
    yield put(authActions.setRefreshToken(null));
    yield put(setPendingCheckoutOnSignup(false));
  } finally {
    yield put(toggleGlobalLoaderQueue({ name: 'authorize', status: false }));
    yield put(authActions.updateLoadingStatus(false));
  }
}

export function* register({ payload }) {
  try {
    yield put(toggleGlobalLoaderQueue({ name: 'register', status: true }));
    yield put(authActions.updateLoadingStatus(true));

    const name = [payload.firstname, payload.lastname]
      .filter(Boolean)
      .join(' ');

    const registerResult = yield call(
      AuthHubClient.register,
      payload.email,
      payload.password,
      name
    );

    if (!registerResult.success) {
      throw new Error(registerResult.message || 'Registration failed');
    }

    try {
      const eatzyTokenResult = yield call(
        AuthHubClient.getEatzyAccessToken,
        payload.email,
        payload.password
      );

      if (eatzyTokenResult.success) {
        yield put(
          authActions.setEatzyTokens({
            accessToken: eatzyTokenResult.accessToken,
            refreshToken: eatzyTokenResult.refreshToken,
            accessExpires: eatzyTokenResult.accessExpires,
          })
        );

        yield put(
          authActions.setAccessToken({
            token: eatzyTokenResult.accessToken,
            expires: eatzyTokenResult.accessExpires,
          })
        );
        yield put(
          authActions.setRefreshToken({
            token: eatzyTokenResult.refreshToken,
            expires: eatzyTokenResult.refreshExpires,
          })
        );
      }
    } catch (eatzyErr) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Auth] Eatzy access token fetch failed (non-blocking):',
        eatzyErr.message
      );
    }

    yield put(authActions.setSessionToken(registerResult.token));
    yield put(authActions.setAuthUser(registerResult.user));

    if (payload.phone) {
      try {
        yield call(accountApi.phone.createPhone, {
          number: payload.phone,
          primary: true,
        });
      } catch (phoneErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Auth] Phone save failed (non-blocking):',
          phoneErr.message
        );
      }
    }

    const user = yield call(apiGetProfile);
    const userId = user?.data?.id || user?.id;

    const currentAddress = yield select(
      (state) => state.address?.selectedAddress
    );
    const needsAddressSave =
      currentAddress &&
      (!currentAddress.address_id ||
        !currentAddress.user_id ||
        currentAddress.user_id !== userId);
    if (needsAddressSave) {
      try {
        const { address, city, state, zipcode, latitude, longitude } =
          currentAddress;
        const createResult = yield call(accountApi.address.createAddress, {
          address,
          city,
          state,
          zip: zipcode,
          latitude,
          longitude,
          primary: true,
        });
        const newAddressId = createResult?.id || createResult?.address?.id;
        if (newAddressId) {
          yield put(
            setSelectedAddress({
              ...currentAddress,
              address_id: newAddressId,
              user_id: userId,
              primary: true,
            })
          );
        }
      } catch (addrErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Auth] Guest address save failed (non-blocking):',
          addrErr.message
        );
      }
    }

    yield all([call(getAddressesSaga), call(getPhonesSaga)]);
    yield put(authActions.updateSuccessStatus(true));
    yield put(updateUser(user));
    yield put(authActions.updateError(null));

    const pendingCheckout = yield select(
      (state) => state.order?.pendingCheckoutOnSignup
    );
    if (pendingCheckout) {
      yield put(setPendingCheckoutOnSignup(false));
      yield put(setCheckoutContinuation(true));
    }

    if (payload && payload?.callback) {
      payload.callback();
    }
    yield put(setNavigateTo(''));
  } catch (error) {
    const errorData = error?.response?.data || error;
    yield put(authActions.updateSuccessStatus(false));
    yield put(authActions.updateError(serializeItem(errorData)));
    yield put(authActions.setSessionToken(null));
    yield put(authActions.setAccessToken(null));
    yield put(authActions.setRefreshToken(null));
    yield put(setPendingCheckoutOnSignup(false));
  } finally {
    yield put(toggleGlobalLoaderQueue({ name: 'register', status: false }));
    yield put(authActions.updateLoadingStatus(false));
  }
}

export function* silentRegisterSaga({ payload }) {
  try {
    yield put(
      toggleGlobalLoaderQueue({ name: 'silentRegister', status: true })
    );
    yield put(authActions.updateLoadingStatus(true));

    const name = [payload.firstname, payload.lastname]
      .filter(Boolean)
      .join(' ');

    const registerResult = yield call(
      AuthHubClient.registerGuest,
      name,
      payload.contact_email
    );

    if (!registerResult.success) {
      throw new Error(registerResult.message || 'Guest registration failed');
    }

    yield put(authActions.setSessionToken(registerResult.token));
    yield put(authActions.setAuthUser(registerResult.user));

    try {
      const eatzyTokenResult = yield call(
        AuthHubClient.getEatzyAccessToken,
        registerResult.syntheticEmail,
        registerResult.tempPassword
      );

      if (eatzyTokenResult.success) {
        yield put(
          authActions.setEatzyTokens({
            accessToken: eatzyTokenResult.accessToken,
            refreshToken: eatzyTokenResult.refreshToken,
            accessExpires: eatzyTokenResult.accessExpires,
          })
        );
        yield put(
          authActions.setAccessToken({
            token: eatzyTokenResult.accessToken,
            expires: eatzyTokenResult.accessExpires,
          })
        );
        yield put(
          authActions.setRefreshToken({
            token: eatzyTokenResult.refreshToken,
            expires: eatzyTokenResult.refreshExpires,
          })
        );
      }
    } catch (eatzyErr) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Auth] Eatzy access token fetch failed (non-blocking):',
        eatzyErr.message
      );
    }

    yield put(
      authActions.setSilentAccountState({
        isSilentAccount: true,
        guestContactEmail: payload.contact_email,
        silentAccountPassword: registerResult.tempPassword,
      })
    );

    if (payload.contact_email) {
      try {
        yield call(updateContactEmail, payload.contact_email);
      } catch (emailErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Auth] Contact email save failed (non-blocking):',
          emailErr.message
        );
      }
    }

    if (payload.phone) {
      try {
        yield call(accountApi.phone.createPhone, {
          number: payload.phone,
          primary: true,
        });
      } catch (phoneErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Auth] Phone save failed (non-blocking):',
          phoneErr.message
        );
      }
    }

    const user = yield call(apiGetProfile);
    const userId = user?.data?.id || user?.id;
    yield put(updateUser(user));

    const currentAddress = yield select(
      (state) => state.address?.selectedAddress
    );
    const needsAddressSave =
      currentAddress &&
      (!currentAddress.address_id ||
        !currentAddress.user_id ||
        currentAddress.user_id !== userId);
    if (needsAddressSave) {
      try {
        const { address, city, state, zipcode, latitude, longitude } =
          currentAddress;
        const createResult = yield call(accountApi.address.createAddress, {
          address,
          city,
          state,
          zip: zipcode,
          latitude,
          longitude,
          primary: true,
        });
        const newAddressId = createResult?.id || createResult?.address?.id;
        if (newAddressId) {
          yield put(
            setSelectedAddress({
              ...currentAddress,
              address_id: newAddressId,
              user_id: userId,
              primary: true,
            })
          );
        }
      } catch (addrErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Auth] Guest address save failed (non-blocking):',
          addrErr.message
        );
      }
    }

    yield all([call(getAddressesSaga), call(getPhonesSaga)]);
    yield put(authActions.updateSuccessStatus(true));
    yield put(authActions.updateError(null));
    yield put(setAddressResponse(null));
    yield put(setCheckoutContinuation(true));
    yield put(setNavigateTo(''));
  } catch (error) {
    notifyBugsnag(error);
    Notification.error('Something went wrong. Please try again.', {
      toastId: 'silent-register-error',
    });
    const errorData = error?.response?.data || error;
    yield put(authActions.updateSuccessStatus(false));
    yield put(authActions.updateError(serializeItem(errorData)));
    yield put(authActions.clearSilentAccountState());
    yield put(authActions.setSessionToken(null));
    yield put(authActions.setAccessToken(null));
    yield put(authActions.setRefreshToken(null));
  } finally {
    yield put(
      toggleGlobalLoaderQueue({ name: 'silentRegister', status: false })
    );
    yield put(authActions.updateLoadingStatus(false));
  }
}

export function* logoutSaga() {
  try {
    const state = yield select();
    const sessionToken = state.authorization?.sessionToken;

    if (sessionToken) {
      try {
        yield call(AuthHubClient.logout, sessionToken);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Auth] Auth Hub logout failed (non-blocking):',
          err.message
        );
      }
    }

    yield put(authActions.clearSilentAccountState());
    yield put(authActions.clearAuth());
    yield put(profileActions.resetAll());
    yield put(mainActions.resetUser());
  } catch (error) {
    yield put(authActions.updateError(serializeItem(error)));
  }
}

const EATZY_RESTORE_BUFFER_MS = 5 * 60 * 1000;

function* tryRestoreFromEatzyToken() {
  const storedEatzyToken = localStorage.getItem('eatzy_access_token');
  const storedEatzyRefresh = localStorage.getItem('eatzy_refresh_token');
  const storedEatzyExpires = localStorage.getItem('eatzy_token_expires');

  const expiresNum = storedEatzyExpires ? Number(storedEatzyExpires) : null;
  const isExpiringSoon = expiresNum
    ? Date.now() + EATZY_RESTORE_BUFFER_MS > expiresNum * 1000
    : false;

  let eatzyToken =
    storedEatzyToken && !isExpiringSoon ? storedEatzyToken : null;
  let eatzyRefresh = storedEatzyRefresh || null;
  let eatzyExpires = expiresNum;

  if (!eatzyToken && storedEatzyRefresh) {
    try {
      const refreshData = yield call(
        AuthHubClient.refreshEatzyToken,
        storedEatzyRefresh
      );
      eatzyToken =
        refreshData?.accessToken ||
        refreshData?.access_token ||
        refreshData?.token ||
        null;
      eatzyRefresh =
        refreshData?.refreshToken ||
        refreshData?.refresh_token ||
        storedEatzyRefresh;
      eatzyExpires = refreshData?.accessExpires || refreshData?.expires || null;
    } catch (refreshErr) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Auth] Eatzy token refresh failed during session restore:',
        refreshErr.message
      );
    }
  }

  if (!eatzyToken) return false;

  yield put(
    authActions.setEatzyTokens({
      accessToken: eatzyToken,
      refreshToken: eatzyRefresh,
      accessExpires: eatzyExpires,
    })
  );
  yield put(
    authActions.setAccessToken({
      token: eatzyToken,
      expires: eatzyExpires,
    })
  );
  if (eatzyRefresh) {
    yield put(
      authActions.setRefreshToken({
        token: eatzyRefresh,
        expires: eatzyExpires,
      })
    );
  }

  try {
    const user = yield call(apiGetProfile);
    yield put(updateUser(user));
    yield all([call(getPhonesSaga), call(getAddressesSaga)]);
  } catch (profileErr) {
    const status = profileErr?.response?.status;
    if (status === 401 || status === 403) {
      // The eatzy token is definitively rejected by the server.
      // Eagerly clear auth state and stale tokens so cleanup is not coupled
      // to caller behaviour, then return false to let the caller show the
      // session-expired toast.
      yield put(authActions.clearAuth());
      return false;
    }
    // Temporary error (network, 5xx) — treat as non-blocking and keep the
    // restore successful so the user isn't unnecessarily logged out.
    // eslint-disable-next-line no-console
    console.warn(
      '[Auth] Profile fetch failed during eatzy-only restore:',
      profileErr.message
    );
  }

  return true;
}

export function* restoreSessionSaga() {
  try {
    const storedToken = localStorage.getItem('authToken');
    if (!storedToken) return;

    const verifyResult = yield call(AuthHubClient.verifyToken, storedToken);

    if (verifyResult.valid) {
      const storedEatzyToken = localStorage.getItem('eatzy_access_token');
      const storedEatzyRefresh = localStorage.getItem('eatzy_refresh_token');
      const storedEatzyExpires = localStorage.getItem('eatzy_token_expires');

      if (storedEatzyToken) {
        const expiresNum = storedEatzyExpires
          ? Number(storedEatzyExpires)
          : null;

        yield put(
          authActions.setEatzyTokens({
            accessToken: storedEatzyToken,
            refreshToken: storedEatzyRefresh,
            accessExpires: expiresNum,
          })
        );

        yield put(
          authActions.setAccessToken({
            token: storedEatzyToken,
            expires: expiresNum,
          })
        );
        if (storedEatzyRefresh) {
          yield put(
            authActions.setRefreshToken({
              token: storedEatzyRefresh,
              expires: expiresNum,
            })
          );
        }
      } else {
        try {
          const eatzyResult = yield call(
            AuthHubClient.getEatzyAccessTokenBySession,
            storedToken
          );

          if (eatzyResult.success) {
            yield put(
              authActions.setEatzyTokens({
                accessToken: eatzyResult.accessToken,
                refreshToken: eatzyResult.refreshToken,
                accessExpires: eatzyResult.accessExpires,
              })
            );
            yield put(
              authActions.setAccessToken({
                token: eatzyResult.accessToken,
                expires: eatzyResult.accessExpires,
              })
            );
            if (eatzyResult.refreshToken) {
              yield put(
                authActions.setRefreshToken({
                  token: eatzyResult.refreshToken,
                  expires: eatzyResult.refreshExpires,
                })
              );
            }
          }
        } catch (eatzyErr) {
          // eslint-disable-next-line no-console
          console.warn(
            '[Auth] Eatzy token fetch failed during restore:',
            eatzyErr.message
          );
        }
      }

      yield put(authActions.setSessionToken(storedToken));
      yield put(authActions.setAuthUser(verifyResult.user));

      try {
        const user = yield call(apiGetProfile);
        yield put(updateUser(user));
        yield all([call(getPhonesSaga), call(getAddressesSaga)]);
      } catch (profileErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Auth] Profile fetch failed during session restore:',
          profileErr.message
        );
      }
    } else {
      const restored = yield call(tryRestoreFromEatzyToken);
      if (!restored) {
        yield put(authActions.clearAuth());
        Notification.warning(
          'Your session has expired. Please log in to continue.',
          { toastId: 'SessionExpired', autoClose: 5000 }
        );
      }
    }
  } catch (err) {
    const restored = yield call(tryRestoreFromEatzyToken);
    if (!restored) {
      yield put(authActions.clearAuth());
      Notification.warning(
        'Your session has expired. Please log in to continue.',
        { toastId: 'SessionExpired', autoClose: 5000 }
      );
    }
  }
}

export function* forgotPasswordSaga({ payload }) {
  try {
    yield put(
      toggleGlobalLoaderQueue({ name: 'forgotPassword', status: true })
    );

    const email = typeof payload === 'string' ? payload : payload.email;
    const callbackUrl =
      typeof payload === 'string' ? undefined : payload.callbackUrl;

    yield call(AuthHubClient.forgotPassword, email, callbackUrl);

    yield put(
      profileActions.forgotPasswordResponse({ response: { success: true } })
    );
  } catch (error) {
    yield put(profileActions.forgotPasswordResponse({ error }));
  } finally {
    yield put(
      toggleGlobalLoaderQueue({ name: 'forgotPassword', status: false })
    );
  }
}

export function* resetPasswordSaga({ payload }) {
  try {
    yield put(toggleGlobalLoaderQueue({ name: 'resetPassword', status: true }));

    yield call(AuthHubClient.resetPassword, payload.token, payload.password);

    yield put(
      profileActions.resetPasswordResponse({ response: { success: true } })
    );
  } catch (error) {
    const errorCode = error?.response?.data?.error;
    const errorMessage =
      errorCode === 'INVALID_TOKEN'
        ? 'This reset link is invalid or has expired — please request a new one.'
        : error?.response?.data?.message || 'Something went wrong.';
    yield put(
      profileActions.resetPasswordResponse({ error: { message: errorMessage } })
    );
  } finally {
    yield put(
      toggleGlobalLoaderQueue({ name: 'resetPassword', status: false })
    );
  }
}

export function* upgradeSilentAccountSaga({ payload }) {
  const { newPassword } = payload;
  try {
    yield put(
      toggleGlobalLoaderQueue({ name: 'upgradeSilentAccount', status: true })
    );

    const state = yield select();
    const currentPassword = state.authorization?.silentAccountPassword;

    yield call(accountApiChangePassword, {
      current_password: currentPassword,
      new_password: newPassword,
    });

    yield put(authActions.clearSilentAccount());
  } catch (error) {
    const errorData = error?.response?.data || error;
    yield put(authActions.updateError(serializeItem(errorData)));
  } finally {
    yield put(
      toggleGlobalLoaderQueue({ name: 'upgradeSilentAccount', status: false })
    );
  }
}

export function* watchAuthorizationSagas() {
  yield takeLeading(authActions.authorizeUser.type, authorize);
  yield takeLeading(authActions.registerUser.type, register);
  yield takeLatest(authActions.silentRegister.type, silentRegisterSaga);
  yield takeLeading(
    authActions.upgradeSilentAccount.type,
    upgradeSilentAccountSaga
  );
  yield takeLeading(authActions.logoutRequest.type, logoutSaga);
  yield takeLeading(authActions.restoreSession.type, restoreSessionSaga);
  // eslint-disable-next-line prettier/prettier
  yield takeLeading(authActions.forgotPasswordRequest.type, forgotPasswordSaga);
  // eslint-disable-next-line prettier/prettier
  yield takeLeading(authActions.resetPasswordRequest.type, resetPasswordSaga);
}
